import {
  useWSSnackbar,
  WSAlert,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  ListLedgerAccountsFilterParameterClassificationEq,
  ReferenceDataRefreshRequestCollectionsInner
} from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData
} from "@query";
import { selectorAccountingVendorOptions } from "../../selectors/selectorAccountingVendorOptions";
import { selectorIsPayeeQboMappingEnabled } from "../../selectors/selectorIsPayeeQboMappingEnabled";
import { selectorLedgerAccountOptions } from "../../selectors/selectorLedgerAccountOptions";
import { selectorQuickbooksConnection } from "../../selectors/selectorQuickbooksConnection";

export interface PayeeQboMappingFormValues {
  qboVendorId: string;
  qboExpenseAccountId: string;
}

export const PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES: PayeeQboMappingFormValues =
  {
    qboExpenseAccountId: "",
    qboVendorId: ""
  };

export interface FormPartialPayeeQboMappingProps {
  disabled?: boolean;
}

export const FormPartialPayeeQboMapping: React.FC<
  FormPartialPayeeQboMappingProps
> = props => {
  const disabled = props.disabled ?? false;
  const { openSnackbar } = useWSSnackbar();
  const queryFeatureFlags = useQueryFeatureFlags();

  // v3 has no single-connection read; the QuickBooks connection is derived from
  // the account's connection list (was v1 `GET /integrations/quickbooks/service`).
  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connection = selectorQuickbooksConnection(queryConnections.data);
  const connectionId = connection?.id;

  // Reference data is scoped to one connection, so both reads wait on it.
  const queryVendors =
    queriesAccountingReferenceData.listAccountingVendors.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: !!connectionId }
    );
  // v1 had a dedicated expense-accounts endpoint; v3 serves one ledger-account
  // collection narrowed by classification.
  const queryExpenseAccounts =
    queriesAccountingReferenceData.listLedgerAccounts.useQueryAll(
      {
        connectionId: connectionId as string,
        filter: {
          classification: {
            eq: ListLedgerAccountsFilterParameterClassificationEq.EXPENSE
          }
        }
      },
      { enabled: !!connectionId }
    );

  // v3 replacement for the v1 resync modal (which drove per-entity "create"
  // mutations and polled): the refresh is an async operation, so it is
  // fire-and-report rather than something to await a fresh list on.
  const [refreshReferenceData] =
    queriesAccountingReferenceData.refreshReferenceData.useMutation();

  const { setValue } = useFormContext<PayeeQboMappingFormValues>();

  useEffect(() => {
    if (disabled) {
      setValue("qboVendorId", "");
      setValue("qboExpenseAccountId", "");
    }
  }, [disabled, setValue]);

  const isEnabled = selectorIsPayeeQboMappingEnabled({
    connection,
    expenseAccounts: queryExpenseAccounts.flatData,
    featureFlags: queryFeatureFlags?.data,
    vendors: queryVendors.flatData
  });

  if (!isEnabled) return null;

  const handleResync = (
    collection: ReferenceDataRefreshRequestCollectionsInner,
    label: string
  ) => {
    if (!connectionId) {
      return;
    }

    refreshReferenceData(
      {
        connectionId,
        referenceDataRefreshRequest: { collections: [collection] }
      },
      {
        onSuccess: () => {
          openSnackbar({
            type: "success",
            duration: 5000,
            message: `Refreshing ${label} from QuickBooks. This can take a moment.`
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            duration: 5000,
            message: `Failed to refresh ${label}`
          });
        }
      }
    );
  };

  const vendorOptions = selectorAccountingVendorOptions(queryVendors.flatData);
  const accountOptions = selectorLedgerAccountOptions(
    queryExpenseAccounts.flatData
  );

  const defaultAccount = (queryExpenseAccounts.flatData ?? []).find(
    account =>
      account.id === connection?.mappingDefaults?.expenseLedgerAccountId
  );

  return (
    <WSList gap="L">
      <WSDivider label="QuickBooks Mapping" />
      {disabled ? (
        <WSAlert theme="warning" title="QBO disabled">
          QuickBooks mapping is per-engagement and can only be set when
          assigning a single engagement. You can always configure mapping later
          from the contractor's details page.
        </WSAlert>
      ) : (
        <WSInfoBox>
          Because you have an active integration with QBO, please map this
          contractor to an existing Vendor, or leave blank and we will create a
          new one. You can also set a default expense account for payable line
          items.
        </WSInfoBox>
      )}
      <WSForm.Field
        name="qboVendorId"
        component={WSSelect}
        componentProps={{
          disabled,
          internalSearch: true,
          menuFooterAction: {
            icon: "refresh-v",
            label: "Resync QBO Vendors",
            onClick: () => {
              handleResync(
                ReferenceDataRefreshRequestCollectionsInner.VENDORS,
                "vendors"
              );
            }
          },
          options: disabled ? [] : vendorOptions,
          placeholder: "Not mapped, create new Vendor"
        }}
        label="QBO Vendor"
      />
      <WSForm.Field
        name="qboExpenseAccountId"
        component={WSSelect}
        componentProps={{
          disabled,
          internalSearch: true,
          menuFooterAction: {
            icon: "refresh-v",
            label: "Resync QBO Expenses Accounts",
            onClick: () => {
              handleResync(
                ReferenceDataRefreshRequestCollectionsInner.LEDGER_ACCOUNTS,
                "expense accounts"
              );
            }
          },
          options: disabled ? [] : accountOptions,
          placeholder: defaultAccount
            ? `Use default "${defaultAccount.displayName}"`
            : "Not mapped"
        }}
        label="Default QBO Expense Account"
      />
    </WSList>
  );
};
